import useAirtable from "../../../../../services/useAirtable";
import type { TSession } from "../../../../../utils/types";
import type { ConsultPatient } from "./useConsultSearch";
import { useEffect, useState } from "react";
import { enrichPatientsWithProfileImages } from "../services/profileImageService";

interface ConsultDataProps {
  currentDateRange: { start: string; end: string };
  clinicId?: string | null;
  session: TSession | null;
  demoMode: boolean;
  useDummyData: boolean;
  selectedPatient: ConsultPatient | null;
  memoizedPatient?: ConsultPatient | null;
}

/**
 * Manages all data fetching for consults
 */
export const useConsultData = ({
  currentDateRange,
  clinicId,
  session,
  demoMode,
  useDummyData,
  selectedPatient,
  memoizedPatient,
}: ConsultDataProps) => {
  const { useMultiDaySchedule, useSimulations } = useAirtable;
  const [scheduleDataWithProfileImages, setScheduleDataWithProfileImages] =
    useState<any[]>([]);
  const [isEnrichingSchedule, setIsEnrichingSchedule] = useState(false);

  // Fetch schedule data
  const {
    scheduleData,
    loading: isLoading,
    error: isError,
    refreshSchedule,
    searchPatients,
    loadMoreDays,
  } = useMultiDaySchedule(
    currentDateRange.start,
    currentDateRange.end,
    7,
    clinicId,
    demoMode,
    session,
    useDummyData,
  );

  useEffect(() => {
    let isActive = true;

    const enrichSchedule = async () => {
      if (!Array.isArray(scheduleData) || scheduleData.length === 0) {
        if (isActive) {
          setScheduleDataWithProfileImages([]);
          setIsEnrichingSchedule(false);
        }
        return;
      }

      // Set initial data immediately without profile images to avoid showing loading state
      if (isActive && scheduleDataWithProfileImages.length === 0) {
        setScheduleDataWithProfileImages(scheduleData);
      }

      if (isActive) {
        setIsEnrichingSchedule(true);
      }

      try {
        const enrichedSections = await Promise.all(
          scheduleData.map(async (section: any) => {
            const sectionPatients = Array.isArray(section.data)
              ? section.data
              : [];
            const enrichedPatients = await enrichPatientsWithProfileImages(
              sectionPatients,
              session,
            );
            return {
              ...section,
              data: enrichedPatients,
            };
          }),
        );

        if (isActive) {
          setScheduleDataWithProfileImages(enrichedSections);
        }
      } finally {
        if (isActive) {
          setIsEnrichingSchedule(false);
        }
      }
    };

    enrichSchedule();

    return () => {
      isActive = false;
    };
  }, [scheduleData, session]);

  // Fetch simulations for selected patient
  const {
    simulations: simulationRows,
    loading: isLoadingSimulations,
    starredSimulations,
    toggleSimulationStar,
  } = useSimulations(selectedPatient);

  // Calculate total appointments
  const totalAppointments = scheduleDataWithProfileImages?.reduce(
    (sum, section) => sum + (section.data?.length || 0),
    0,
  );
  const isScheduleLoading = isLoading || isEnrichingSchedule;

  return {
    // Schedule data
    scheduleData: scheduleDataWithProfileImages,
    isLoading: isScheduleLoading,
    isError,
    refreshSchedule,
    searchPatients,
    loadMoreDays,
    totalAppointments,

    // Simulation data
    simulationRows,
    isLoadingSimulations,
    starredSimulations,
    toggleSimulationStar,
  };
};
